import { useRef, useState, useEffect, useLayoutEffect } from "react";
import styled from "@emotion/styled";
import { surface, border as borderToken, shadow, borderRadius, spacing, zIndex, layout } from "@atlas-int/tokens";
import SettingsRadioGroup from "./SettingsRadioGroup";
import { DefaultSettingsButton } from "./styles";
import type { SelectBoxItem } from "../dropdownPrimitive/types";

// ─── Styled components ──────────────────────────────────────────────────────

const DropdownPanel = styled.div<{ $alignRight: boolean }>`
  position: absolute;
  top: calc(100% + ${spacing[1]});
  ${({ $alignRight }) => ($alignRight ? "right: 0;" : "left: 0;")}
  z-index: ${zIndex.settingsDropdown};
  min-width: 200px;
  max-height: 70vh;
  overflow-y: auto;
  background: ${surface.raised};
  border-radius: ${borderRadius.base};
  box-shadow: ${shadow.sm};
  border: 1px solid ${borderToken.control};
  padding: ${spacing[2]} 0;
`;

const DropdownFooter = styled.div`
  padding: ${spacing[4]} ${spacing[6]};
  border-top: 1px solid ${borderToken.subtle};
  display: flex;
  justify-content: center;
`;

// ─── Types ──────────────────────────────────────────────────────────────────

export interface SettingsGroup {
  label: string;
  /** Tooltip explanation shown on hover via an info icon next to the label */
  tooltip?: string;
  items: SelectBoxItem[];
  selectedValue: string | number | undefined;
  onChange: (value: string | number) => void;
  disabled?: boolean;
}

// ─── Component ──────────────────────────────────────────────────────────────

interface CompactSettingsDropdownProps {
  onClose: () => void;
  /** Settings groups to render as radio groups */
  groups: SettingsGroup[];
  /** Additional content rendered after radio groups (e.g. sliders) */
  extraContent?: React.ReactNode;
  /** Callback for the "Default Settings" button */
  onResetDefaults?: () => void;
}

const CompactSettingsDropdown = ({
  onClose,
  groups,
  extraContent,
  onResetDefaults,
}: CompactSettingsDropdownProps) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const [alignRight, setAlignRight] = useState(true);

  // Decide alignment based on available space
  useLayoutEffect(() => {
    const el = panelRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const overflowRight = rect.right > window.innerWidth;
    const overflowLeft = rect.left < 0;
    if (overflowRight && !overflowLeft) setAlignRight(true);
    else if (overflowLeft && !overflowRight) setAlignRight(false);
  }, []);

  // After alignment settles, clamp edges to viewport so the panel never hides behind the sidebar
  useLayoutEffect(() => {
    const el = panelRef.current;
    if (!el) return;
    const margin = 8;
    const rect = el.getBoundingClientRect();
    if (rect.left < margin) {
      el.style.left = `${margin - rect.left}px`;
      el.style.right = "auto";
    } else if (rect.right > window.innerWidth - margin) {
      el.style.right = `${rect.right - window.innerWidth + margin}px`;
      el.style.left = "auto";
    }
  }, [alignRight]);

  // Close when clicking outside (but not the settings toggle button itself)
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // Don't close if clicking the toggle button -- let its onClick handle it
      if (target.closest("[data-settings-toggle]")) return;
      if (panelRef.current && !panelRef.current.contains(target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  return (
    <DropdownPanel ref={panelRef} $alignRight={alignRight}>
      {groups.map((group) => (
        <SettingsRadioGroup
          key={group.label}
          label={group.label}
          tooltip={group.tooltip}
          items={group.items}
          selectedValue={group.selectedValue}
          onChange={group.onChange}
        />
      ))}
      {extraContent}
      {onResetDefaults && (
        <DropdownFooter>
          <DefaultSettingsButton variant="default" size="lg" onClick={onResetDefaults}>
            Default Settings
          </DefaultSettingsButton>
        </DropdownFooter>
      )}
    </DropdownPanel>
  );
};

export default CompactSettingsDropdown;
